<script lang="ts" setup>
  import { ref, watch, onMounted, computed } from 'vue';
	import { useTeamStore } from '@/store/team.store';
	import { useAuthStore } from '@/store/auth.store';
	import { IUser } from '@/types';
	import { useModalStore } from '@/store/modal.store';
	import DefaultBtn from "@/components/block/DefaultBtn.vue";
  import { useRoute } from "vue-router";

	const modalStore = useModalStore();
	const teamStore = useTeamStore();
	const authStore = useAuthStore();
	type IGroup = any;
	const companies = ref<IGroup[]>([]);
  const route = useRoute();
  const loading = ref<boolean>(false)
  const groupId = computed<string>(() => {
  // If payload has a group ID, use it
  if (modalStore.modalPayload?.id) {
    return String(modalStore.modalPayload.id);
  }
  
  // If route has a group ID, use it
  if (route.params.groupId) {
    return String(route.params.groupId);
  }
  
  // If modalPayload has a selectedTeam, use it
  if (modalStore.modalPayload?.selectedTeam) {
    return String(modalStore.modalPayload.selectedTeam);
  }
  
  // Otherwise, use empty string
  return '';
});

	const isSuperAdmin = computed(() => !!authStore.user.permissions?.admin && !authStore.user.group);

	const selectedCompanyName = computed(() => groupId.value ? (teamStore.groupsMap.get(groupId.value)?.name || '') : '');

	const canAddUserForSuperAdmin = computed(() => !isSuperAdmin.value || !!groupId.value);

	onMounted(() => {
		teamStore.fetchTeam().then(() => {
			companies.value = teamStore.groups;
		});
	});

	const props = defineProps({
		closeSignal: {
			type: Boolean,
			required: false
		}
	});

	const emit = defineEmits(['page-changed', 'items-per-page-changed']);
	const isOpen = ref(false);

	const toggleSelect = () => {
		isOpen.value = !isOpen.value;
	};

	watch(() => props.closeSignal, () => {
		if (props.closeSignal) {
      close();
		}
	});

	function generateStrongPassword(length: number): string {
		const upperCase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
		const lowerCase = 'abcdefghijklmnopqrstuvwxyz';
		const numbers = '0123456789';
		const specialChars = '!@#$%^&*()_+[]{}|;:,.<>?';

		const allChars = upperCase + lowerCase + numbers + specialChars;
		let password = '';

		for (let i = 0; i < length; i++) {
			const randomIndex = Math.floor(Math.random() * allChars.length);
			password += allChars[randomIndex];
		}

		const ensureContains = (chars: string) => {
			return password.split('').some(char => chars.includes(char));
		};

		if (!ensureContains(upperCase)) {
			password += upperCase[Math.floor(Math.random() * upperCase.length)];
		}
		if (!ensureContains(lowerCase)) {
			password += lowerCase[Math.floor(Math.random() * lowerCase.length)];
		}
		if (!ensureContains(numbers)) {
			password += numbers[Math.floor(Math.random() * numbers.length)];
		}
		if (!ensureContains(specialChars)) {
			password += specialChars[Math.floor(Math.random() * specialChars.length)];
		}

		password = password.split('').sort(() => Math.random() - 0.5).join('');

		return password;
	}

	const newUser = ref<IUser>({
		id: '',
		img: '',
		email: '',
		name: '',
		phone: '',
		middleName: '',
		lastName: '',
		corporateId: '',
		group: groupId.value,
		role: 'user',
		permissions: {}
	});


	const clearUser = (): void => {
		newUser.value.id = '';
		newUser.value.img = '';
		newUser.value.email = '';
		newUser.value.name = '';
		newUser.value.phone = '';
		newUser.value.middleName = '';
		newUser.value.lastName = '';
		newUser.value.corporateId = '';
		newUser.value.group = groupId.value || '';
		newUser.value.role = 'user';
		newUser.value.permissions = {};

		step1();
	};

	// Sync newUser.group when modal opens with pre-selected team
	watch(() => modalStore.isModalVisible('NewUser'), (visible) => {
		if (visible && groupId.value) {
			newUser.value.group = groupId.value;
		}
	});

	watch(() => newUser.value.phone, (current, prev) => {
		if (current !== prev && newUser.value.phone) {
			const digitsOnly = newUser.value.phone.replace(/\D/g, '');
			newUser.value.phone = ('+1' + digitsOnly.replace(/^1/, '')).slice(0, 12);
		}
	})

	const createUser = (): void => {
		loading.value = true;
		teamStore.createUser(newUser.value, addPassword.value)
			.then(() => {
				step3();
			})
			.finally(() => {
				loading.value = false;
			});
	};

	const visiblePassword = ref(false);
	const togglePasswordVisibility = () => {
		visiblePassword.value = !visiblePassword.value;
	};

	const addPassword = ref(generateStrongPassword(10));
	const confirmPasswordError = ref("");

	const copyPassword = () => {
		if (addPassword.value) {
			navigator.clipboard.writeText(addPassword.value).then(() => {
				console.log('Password copied to clipboard');
			}).catch(err => {
				console.log('Failed to copy password: ' + err);
			});
		} else {
			console.log('No password to copy');
		}
	};

	const currentStep = ref<string>('step1');
	const previousStep = ref<string | null>(null);
	const nameError = ref("");
	const middleNameError = ref("");
	const lastNameError = ref("");
	const emailError = ref("");
	const phoneError = ref("");

	function validateStep1(): boolean {
		let isValid = true;

		if (!newUser.value.name) {
			nameError.value = "Fill in the field";
			isValid = false;
		} else {
			nameError.value = "";
		}

		if (!newUser.value.lastName) {
			lastNameError.value = "Fill in the field";
			isValid = false;
		} else {
			lastNameError.value = "";
		}

		return isValid;
	}

	function validateStep2(): boolean {
		let isValid = true;

		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!newUser.value.email || !emailPattern.test(newUser.value.email)) {
			emailError.value = "Please enter a valid email";
			isValid = false;
		} else {
			emailError.value = "";
		}

		const phonePattern = /^\+1\d{10}$/;

		if (!newUser.value.phone || !phonePattern.test(newUser.value.phone)) {
			phoneError.value = "Please enter a valid phone number";
			isValid = false;
		} else {
			phoneError.value = "";
		}

		return isValid;
	}

	function step1() {
		modalStore.openModal('NewUser');
		currentStep.value = 'step1';
	}

	function step2() {
		if (validateStep1()) {
			modalStore.openModal('NewUserStep2');
			currentStep.value = 'step2';
		}
	}

	function step3() {
		if (validateStep2()) {
			modalStore.openModal('NewUserStep3');
		}
	}

	function step4() {
  if (currentStep.value === 'step1' || currentStep.value === 'step2') {
    previousStep.value = currentStep.value;
  } else {
    previousStep.value = null;
  }
  modalStore.openModal('NewUserStep4');
  currentStep.value = 'step4';
}

	function handleContinue() {
		if (validateStep2()) {
			createUser();
		}
	}

	function goBackToPreviousStep() {
		if (previousStep.value) {
			modalStore.closeModal();
			currentStep.value = previousStep.value;
			previousStep.value = null;

			if (currentStep.value === 'step1') {
				step1();
			} else if (currentStep.value === 'step2') {
				step2();
			}
		} else {
			step1();
		}
	}

  const close = ():void => {
    if (!loading.value) {
      modalStore.closeModal();
    }
  };

</script>

<template>
	<div v-if="modalStore.isModalVisible('NewUser')" class="modal modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">person</span>
					<span>New User</span>
				</p>
				<button @click="step4" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="step2">
					<div v-if="isSuperAdmin && groupId" class="login__item login__item--message">
						<span class="material-symbols-outlined login__item_ico">business</span>
						<p class="new-user-company-label">Adding to <strong>{{ selectedCompanyName || groupId }}</strong></p>
					</div>
					<div class="login__item">
						<input
							v-model="newUser.name"
							class="input"
							placeholder="First Name*"
						/>
						<span class="material-symbols-outlined login__item_ico">person</span>
						<p class="error" v-if="nameError">{{ nameError }}</p>
					</div>

					<div class="login__item">
						<input
							v-model="newUser.middleName"
							class="input"
							placeholder="Middle Name"
						/>
						<span class="material-symbols-outlined login__item_ico">person</span>

					</div>

					<div class="login__item">
						<input
							v-model="newUser.lastName"
							class="input"
							placeholder="Last Name*"
						/>
						<span class="material-symbols-outlined login__item_ico">person</span>
						<p class="error" v-if="lastNameError">{{ lastNameError }}</p>
					</div>

					<div class="login__btn-row">
						<button class="login__btn" type="submit" :disabled="!canAddUserForSuperAdmin">Continue</button>
					</div>
				</form>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('NewUserStep2')" class="modal modal--profile modal--active">
		<div class="modal__body">
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">person</span>
					<span>New User</span>
				</p>
				<button @click="step4" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<form @submit.prevent="handleContinue">
					<div class="login__item">
						<input
							v-model="newUser.email"
							class="input"
							placeholder="Email*"
						/>
						<span class="material-symbols-outlined login__item_ico">mail</span>
						<p class="error" v-if="emailError">{{ emailError }}</p>
					</div>

					<div class="login__item">
						<input
							v-model="newUser.phone"
							class="input"
							placeholder="Phone Number*"
						/>
						<span class="material-symbols-outlined login__item_ico">phone</span>
						<p class="error" v-if="phoneError">{{ phoneError }}</p>
					</div>

					<div class="btn__wrap">
						<DefaultBtn class="login__btn btn--white" :neutral="true" text="Previous" @click="step1" />
						<DefaultBtn class="login__btn" :loading text="Confirm" type="submit" :disabled="!canAddUserForSuperAdmin" />
					</div>
				</form>
			</div>
		</div>
	</div>

	<div v-if="modalStore.isModalVisible('NewUserStep3')" class="modal modal--profile modal--active">
		<div class="modal__body" >
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">person</span>
					<span>New User</span>
				</p>
				<button @click="close" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg  class="modal__update_svg" width="94" height="94" viewBox="0 0 94 94" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M40.0532 57.505L28.6823 46.1341C28.0046 45.4564 27.1527 45.1096 26.1267 45.094C25.1007 45.0782 24.2331 45.425 23.524 46.1341C22.815 46.8432 22.4604 47.7029 22.4604 48.7132C22.4604 49.7234 22.815 50.5831 23.524 51.2922L36.9563 64.7245C37.8411 65.6093 38.8734 66.0517 40.0532 66.0517C41.233 66.0517 42.2652 65.6093 43.15 64.7245L70.3817 37.4928C71.0595 36.8151 71.4062 35.9633 71.4219 34.9373C71.4376 33.9112 71.0909 33.0437 70.3817 32.3346C69.6726 31.6255 68.813 31.271 67.8027 31.271C66.7924 31.271 65.9327 31.6255 65.2236 32.3346L40.0532 57.505ZM47.0082 93.5C40.5768 93.5 34.5316 92.2796 28.8727 89.8387C23.2136 87.3979 18.291 84.0854 14.105 79.9012C9.9188 75.7169 6.60477 70.7965 4.16286 65.1399C1.72096 59.4835 0.5 53.4396 0.5 47.0082C0.5 40.5768 1.72042 34.5316 4.16127 28.8727C6.60212 23.2136 9.91464 18.291 14.0988 14.105C18.2831 9.91881 23.2035 6.60478 28.8601 4.16287C34.5165 1.72096 40.5604 0.5 46.9918 0.5C53.4232 0.5 59.4684 1.72043 65.1273 4.16128C70.7864 6.60213 75.709 9.91464 79.895 14.0988C84.0812 18.2831 87.3952 23.2035 89.8371 28.8601C92.279 34.5165 93.5 40.5604 93.5 46.9918C93.5 53.4232 92.2796 59.4683 89.8387 65.1273C87.3979 70.7864 84.0854 75.709 79.9012 79.895C75.7169 84.0812 70.7965 87.3952 65.1399 89.8371C59.4835 92.279 53.4396 93.5 47.0082 93.5ZM47 86.158C57.9316 86.158 67.1908 82.3646 74.7777 74.7777C82.3646 67.1908 86.158 57.9316 86.158 47C86.158 36.0684 82.3646 26.8092 74.7777 19.2223C67.1908 11.6354 57.9316 7.842 47 7.842C36.0684 7.842 26.8092 11.6354 19.2223 19.2223C11.6354 26.8092 7.842 36.0684 7.842 47C7.842 57.9316 11.6354 67.1908 19.2223 74.7777C26.8092 82.3646 36.0684 86.158 47 86.158Z" fill="#2563EB"/>
					<path d="M40.0532 57.505L28.6823 46.1341C28.0046 45.4564 27.1527 45.1096 26.1267 45.094C25.1007 45.0782 24.2331 45.425 23.524 46.1341C22.815 46.8432 22.4604 47.7029 22.4604 48.7132C22.4604 49.7234 22.815 50.5831 23.524 51.2922L36.9563 64.7245C37.8411 65.6093 38.8734 66.0517 40.0532 66.0517C41.233 66.0517 42.2652 65.6093 43.15 64.7245L70.3817 37.4928C71.0595 36.8151 71.4062 35.9633 71.4219 34.9373C71.4376 33.9112 71.0909 33.0437 70.3817 32.3346C69.6726 31.6255 68.813 31.271 67.8027 31.271C66.7924 31.271 65.9327 31.6255 65.2236 32.3346L40.0532 57.505ZM47.0082 93.5C40.5768 93.5 34.5316 92.2796 28.8727 89.8387C23.2136 87.3979 18.291 84.0854 14.105 79.9012C9.9188 75.7169 6.60477 70.7965 4.16286 65.1399C1.72096 59.4835 0.5 53.4396 0.5 47.0082C0.5 40.5768 1.72042 34.5316 4.16127 28.8727C6.60212 23.2136 9.91464 18.291 14.0988 14.105C18.2831 9.91881 23.2035 6.60478 28.8601 4.16287C34.5165 1.72096 40.5604 0.5 46.9918 0.5C53.4232 0.5 59.4684 1.72043 65.1273 4.16128C70.7864 6.60213 75.709 9.91464 79.895 14.0988C84.0812 18.2831 87.3952 23.2035 89.8371 28.8601C92.279 34.5165 93.5 40.5604 93.5 46.9918C93.5 53.4232 92.2796 59.4683 89.8387 65.1273C87.3979 70.7864 84.0854 75.709 79.9012 79.895C75.7169 84.0812 70.7965 87.3952 65.1399 89.8371C59.4835 92.279 53.4396 93.5 47.0082 93.5ZM47 86.158C57.9316 86.158 67.1908 82.3646 74.7777 74.7777C82.3646 67.1908 86.158 57.9316 86.158 47C86.158 36.0684 82.3646 26.8092 74.7777 19.2223C67.1908 11.6354 57.9316 7.842 47 7.842C36.0684 7.842 26.8092 11.6354 19.2223 19.2223C11.6354 26.8092 7.842 36.0684 7.842 47C7.842 57.9316 11.6354 67.1908 19.2223 74.7777C26.8092 82.3646 36.0684 86.158 47 86.158Z" fill="black" fill-opacity="0.2"/>
				</svg>

				<p class="modal__update"> {{ [newUser.name, newUser.middleName, newUser.lastName].filter(Boolean).join(' ') }}  was successfully added.</p>
				<p class="modal__update_text">A default password has been generated for this account and sent to the new user’s email address. Use this password for the initial login. Upon first access, the user will set a new, permanent password.</p>
				<div class="login__item">
				<input
					readonly
					:type="visiblePassword ? 'text' : 'password'"
					class="login__password"
					placeholder="Add password"
					:value="addPassword">
				<span class="material-symbols-outlined login__item_ico">lock</span>
				<button class="login__item_btn" @click="togglePasswordVisibility" type="button">
					<span class="material-symbols-outlined login__item_eyes eyes--off" :class="{ 'login__item_eyes--active': !visiblePassword }">visibility_off</span>
					<span class="material-symbols-outlined login__item_eyes eyes--visible" :class="{ 'login__item_eyes--active': visiblePassword }">visibility</span>
				</button>
				<p class="error" v-if="confirmPasswordError">{{ confirmPasswordError }}</p>
			</div>
			<div class="modal__update_btn">
				<div class="btn__wrap">
					<button class="login__btn btn__img" type="button" @click="clearUser">
						<span class="material-symbols-outlined">add</span>
						Add New User
					</button>
					<button class="login__btn btn__img" type="button" @click="copyPassword">
						<span class="material-symbols-outlined">content_copy</span>
						Copy Password
					</button>
				</div>
			</div>
		</div>
	</div>
</div>

	<div v-if="modalStore.isModalVisible('NewUserStep4')" class="modal modal--profile modal--active">
		<div class="modal__body" >
			<div class="modal__header">
				<p class="modal__subtitle">
					<span class="material-symbols-outlined">person</span>
					<span>New User</span>
				</p>
				<button @click="close" class="modal__close">
					<span class="material-symbols-outlined">close</span>
				</button>
			</div>
			<div class="modal__main">
				<svg class="modal__update_svg" width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M49.9429 79.0138C51.4181 79.0138 52.6642 78.5045 53.681 77.4859C54.6977 76.4673 55.206 75.2204 55.206 73.7451C55.206 72.2699 54.6967 71.0239 53.6781 70.0072C52.6595 68.9904 51.4126 68.482 49.9373 68.482C48.4621 68.482 47.2161 68.9913 46.1994 70.0099C45.1826 71.0285 44.6742 72.2754 44.6742 73.7507C44.6742 75.2259 45.1835 76.472 46.2021 77.4888C47.2207 78.5055 48.4676 79.0138 49.9429 79.0138ZM50.0084 99.0844C43.2197 99.0844 36.8387 97.7962 30.8653 95.2197C24.8919 92.6433 19.6959 89.1467 15.2773 84.7301C10.8585 80.3134 7.36041 75.1196 4.78285 69.1488C2.20529 63.1782 0.916504 56.7985 0.916504 50.0098C0.916504 43.2212 2.20473 36.8402 4.78117 30.8668C7.35762 24.8934 10.8542 19.6973 15.2708 15.2787C19.6875 10.86 24.8813 7.36188 30.852 4.78432C36.8227 2.20676 43.2024 0.917969 49.9911 0.917969C56.7797 0.917969 63.1607 2.2062 69.1341 4.78264C75.1075 7.35909 80.3036 10.8556 84.7222 15.2723C89.1409 19.689 92.639 24.8827 95.2166 30.8535C97.7941 36.8242 99.0829 43.2039 99.0829 49.9925C99.0829 56.7812 97.7947 63.1622 95.2183 69.1355C92.6418 75.109 89.1453 80.305 84.7286 84.7236C80.3119 89.1423 75.1182 92.6405 69.1474 95.218C63.1767 97.7956 56.797 99.0844 50.0084 99.0844ZM49.9997 91.3345C61.5386 91.3345 71.3122 87.3304 79.3205 79.322C87.3289 71.3137 91.3331 61.5401 91.3331 50.0012C91.3331 38.4623 87.3289 28.6887 79.3205 20.6803C71.3122 12.672 61.5386 8.66784 49.9997 8.66784C38.4608 8.66784 28.6872 12.672 20.6789 20.6803C12.6705 28.6887 8.66638 38.4623 8.66638 50.0012C8.66638 61.5401 12.6705 71.3137 20.6789 79.322C28.6872 87.3304 38.4608 91.3345 49.9997 91.3345ZM50.4182 27.8838C52.802 27.8838 54.865 28.6343 56.6071 30.1351C58.3492 31.6359 59.2203 33.5119 59.2203 35.7631C59.2203 37.6576 58.6639 39.3533 57.5511 40.8502C56.4382 42.3473 55.1665 43.7416 53.7357 45.0333C51.8602 46.6971 50.209 48.5272 48.7819 50.5238C47.355 52.5203 46.5918 54.744 46.4924 57.1949C46.4593 58.1354 46.7905 58.9236 47.486 59.5595C48.1815 60.1954 48.9929 60.5133 49.9203 60.5133C50.9138 60.5133 51.755 60.1821 52.4439 59.5197C53.1327 58.8574 53.5732 58.0459 53.7653 57.0855C54.1098 55.3103 54.8434 53.7288 55.9662 52.3412C57.0889 50.9534 58.3052 49.6338 59.615 48.3825C61.4979 46.5471 63.1415 44.5459 64.5457 42.3788C65.95 40.2116 66.6521 37.7945 66.6521 35.1272C66.6521 31.0337 65.0392 27.6704 61.8134 25.0374C58.5876 22.4044 54.8219 21.0879 50.5164 21.0879C47.4429 21.0879 44.545 21.7685 41.8226 23.1297C39.1002 24.4909 36.9805 26.4764 35.4636 29.0863C34.9933 29.8877 34.8423 30.7313 35.0107 31.617C35.179 32.5027 35.6336 33.1752 36.3743 33.6345C37.3171 34.1593 38.2853 34.3091 39.2789 34.0838C40.2724 33.8587 41.117 33.2924 41.8125 32.3849C42.8591 31.0269 44.1259 29.9373 45.613 29.116C47.1 28.2945 48.7018 27.8838 50.4182 27.8838Z" fill="#FF3F6D"/>
				</svg>

				<p class="modal__update">Are you sure?</p>
				<p class="modal__update_text">You are about to close this page without completing the<br> onboarding process. Your work will not be saved.</p>
				<div class="modal__update_btn">
					<button class="btn btn--white btn--border" type="button" @click="close">Close</button>
					<button class="btn" type="submit"  @click="goBackToPreviousStep">Continue</button>
				</div>
			</div>
		</div>
	</div>

</template>


<style scoped>
	.login__item_ico{
		top: 50%;
	}
	.login__select {
		appearance: auto;
		cursor: pointer;
	}
	.modal--profile .modal__body{
		max-width: 615px;
	}

	.btn__wrap{
		display: flex;
		justify-content: center;
		gap: 10px;
	}
	.btn__wrap button {
		width: 160px;
		margin: 0;

	}
	.btn--white{
		border: 1px solid #E2E8F0;
		background: none;
		color:  #0F172A
	}

	.login__btn{
		box-shadow: none;
		min-width: 160px;
	}
	.login__btn:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
	.login__item--message .new-user-company-label {
		margin: 0;
		font-size: 14px;
	}
	.login__item--message {
		color: #1e293b;
	}
	.login__btn-row {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
	}
	.btn__img{
		gap: 10px;
	}

	.pagination{
		display: flex;
		justify-content: space-between;
		border-top: 2px solid #F1F5F9;
		padding-top: 15px;
		color: #94a3b8;
	}
	.pagination__list{
		display: flex;
		align-items: center;
		gap: 5px;
	}
	.pagination__number{
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 50px;
		height: 50px;
		border-radius: 8px;
		border: 1px solid #94a3b8;
		padding: 15px;
		font-size: 16px;
		color: #94a3b8;
		cursor: pointer;
		transition: all .6s;
	}
	.pagination__number:hover{
		background-color: #f0f0f0;
	}
	.pagination__number--active{
		background-color: #f0f0f0;
	}
	.pagination__prev i {
		transition: all .6s;
	}
	.pagination__prev i {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-30%, -50%) rotate(135deg);
		border: solid black;
		border-width: 0 1px 1px 0;
		display: inline-block;
		padding: 4px;
	}
	.pagination__next i {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-80%, -50%) rotate(-45deg);
		border: solid black;
		border-width: 0 1px 1px 0;
		display: inline-block;
		padding: 4px;
	}
	.pagination__select {
		display: flex;
		align-items: center;
		gap: 8px;
	}
	.pagination__select p {
		font-size: 16px;
		font-weight: 600;
		color: #0F172A;
	}

	.pagination__number--disabled:hover {
		background-color: #fff;
		color: #94a3b8;
	}
	.hover:hover {
		background-color: #fff;
	}


	.custom-select {
		position: relative;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		border: 1px solid #93C5FD;
		padding: 15px 20px 15px 50px;
		width: 333px;
		height: 55px;
		border-radius: 8px;
		color: #94a3b8;
	}
	.custom-select ul {
		position: absolute;
		left: 0;
		top: 100%;
		width: 100%;
		z-index: 1;
		display: none;
		background-color: #fff;
		border: 1px solid #ccc;
		box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
		padding: 0;
		margin: 0;
		border-radius: 8px;
	}

	.custom-select ul.show {
		display: block;
	}

	.custom-select ul li {
		list-style: none;
		padding: 8px 12px;
		transition: all .3s;
	}

	.custom-select ul li:hover {
		background-color: #F8FAFC;
	}

	.custom-select ul li.active {
		background-color: #f0f0f0;
	}
	.show{
		overflow: auto;
		height: 300px;
	}
</style>